import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Tag, Modal, Select, message, Space, Button, Popconfirm } from 'antd';
import { ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  getAuditHolders, getAssignableAuditRoles, searchTenantUsers,
  grantAuditRole, revokeAuditRole,
  type AuditRole, type AuditHolder, type AuditUser,
} from '../../services/auditAccessService';

// Audit access (platform bootstrap). A KaziPeople platform operator grants the
// audit role family (Auditor / Compliance Officer / Audit Administrator) to a
// user INSIDE this tenant. This exists because a tenant owner cannot self-grant
// these roles (anti-escalation blocks handing out the sensitive-audit perms they
// don't themselves hold) — so the first audit-access holder is bootstrapped here.
// Backend is whitelisted to the audit family + gated on tenant:update:all.

const ROLE_COLOR: Record<string, string> = {
  auditor: 'blue',
  compliance_officer: 'green',
  audit_administrator: 'gold',
};
const roleLabel = (roles: AuditRole[], key: string) => roles.find((r) => r.key === key)?.label ?? key;

export const TenantAuditAccess: React.FC<{ tenantId: string; canManage: boolean }> = ({ tenantId, canManage }) => {
  const [holders, setHolders] = useState<AuditHolder[]>([]);
  const [roles, setRoles] = useState<AuditRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [grantOpen, setGrantOpen] = useState(false);
  const [users, setUsers] = useState<AuditUser[]>([]);
  const [selUser, setSelUser] = useState<string>();
  const [selRole, setSelRole] = useState<string>();
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, r] = await Promise.all([getAuditHolders(tenantId), getAssignableAuditRoles(tenantId).catch(() => [])]);
      setHolders(h.holders);
      setRoles(r);
    } catch {
      message.error('Could not load audit access.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const openGrant = async () => {
    setSelUser(undefined);
    setSelRole(undefined);
    setGrantOpen(true);
    try {
      setUsers(await searchTenantUsers(tenantId));
    } catch {
      message.error('Could not load tenant users.');
    }
  };

  const doGrant = async () => {
    if (!selUser || !selRole) return;
    setBusy(true);
    try {
      const res = await grantAuditRole(tenantId, selUser, selRole);
      message.success(res.created ? 'Audit access granted.' : 'User already had that role.');
      setGrantOpen(false);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Grant failed.');
    } finally {
      setBusy(false);
    }
  };

  const doRevoke = async (userId: string, roleKey: string) => {
    try {
      await revokeAuditRole(tenantId, userId, roleKey);
      message.success('Audit access revoked.');
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Revoke failed.');
    }
  };

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        label: `${u.fullName || u.email || u.id}${u.email ? ` · ${u.email}` : ''}${
          u.auditRoles.length ? ` (has: ${u.auditRoles.map((k) => roleLabel(roles, k)).join(', ')})` : ''
        }`,
      })),
    [users, roles],
  );

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_: unknown, h: AuditHolder) => (
        <div>
          <div className="text-sm font-medium text-foreground">{h.fullName || '—'}</div>
          <div className="text-xs text-muted-foreground">{h.email || h.userId}</div>
        </div>
      ),
    },
    {
      title: 'Audit roles',
      key: 'roles',
      render: (_: unknown, h: AuditHolder) => (
        <Space size={[4, 4]} wrap>
          {h.roles.map((k) => (
            <Tag key={k} color={ROLE_COLOR[k] || 'default'}>
              {roleLabel(roles, k)}
            </Tag>
          ))}
        </Space>
      ),
    },
    ...(canManage
      ? [
          {
            title: '',
            key: 'actions',
            align: 'right' as const,
            render: (_: unknown, h: AuditHolder) => (
              <Space size={[4, 4]} wrap>
                {h.roles.map((k) => (
                  <Popconfirm
                    key={k}
                    title={`Revoke ${roleLabel(roles, k)}?`}
                    onConfirm={() => doRevoke(h.userId, k)}
                    okText="Revoke"
                    okButtonProps={{ danger: true }}
                  >
                    <Button size="small" danger type="text">
                      Revoke {roleLabel(roles, k)}
                    </Button>
                  </Popconfirm>
                ))}
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-muted-foreground" />
          <div>
            <CardTitle>Audit access</CardTitle>
            <p className="text-xs text-muted-foreground mt-1 max-w-xl">
              Grant the audit roles to a user in this tenant. These roles unlock the tenant's Audit &amp;
              Compliance Center — including graduated access to sensitive salary, bank and security values.
              Bootstrapped here because a tenant owner cannot self-grant them.
            </p>
          </div>
        </div>
        {canManage && (
          <Button type="primary" onClick={openGrant}>
            Grant access
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <Table
          rowKey="userId"
          size="small"
          loading={loading}
          columns={columns as any}
          dataSource={holders}
          pagination={false}
          locale={{ emptyText: 'No one in this tenant has audit access yet.' }}
        />
      </CardContent>

      <Modal
        title="Grant audit access"
        open={grantOpen}
        onCancel={() => setGrantOpen(false)}
        onOk={doGrant}
        okText="Grant"
        confirmLoading={busy}
        okButtonProps={{ disabled: !selUser || !selRole }}
      >
        <div className="space-y-3 py-2">
          <div>
            <label className="text-sm text-muted-foreground">User</label>
            <Select
              showSearch
              placeholder="Search by name or email"
              className="w-full"
              value={selUser}
              onChange={setSelUser}
              options={userOptions}
              optionFilterProp="label"
              filterOption={(input, opt) => (opt?.label as string).toLowerCase().includes(input.toLowerCase())}
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Audit role</label>
            <Select
              placeholder="Select a role"
              className="w-full"
              value={selRole}
              onChange={setSelRole}
              options={roles.map((r) => ({ value: r.key, label: r.label, title: r.description || undefined }))}
            />
            {selRole && (
              <p className="text-xs text-muted-foreground mt-1">
                {roles.find((r) => r.key === selRole)?.description}
              </p>
            )}
          </div>
        </div>
      </Modal>
    </Card>
  );
};

export default TenantAuditAccess;
